import { Badge, Box, Button, Drawer, Group, Loader, Stack, Tabs, Text } from '@mantine/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export type EditorTab = {
    value: string;
    label: string;
    dirty?: boolean;
};

type Props = {
    opened: boolean;
    zIndex?: number;
    suspended?: boolean;
    onClose: () => void;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    status?: { label: string; color?: string };
    dirty?: boolean;
    loading?: boolean;
    tabs?: EditorTab[];
    activeTab?: string;
    onTabChange?: (value: string) => void;
    /** Footer actions for the active tab (Save / Cancel etc). */
    footer?: React.ReactNode;
    headerRight?: React.ReactNode;
    size?: 'md' | 'lg' | 'xl' | string;
    children: React.ReactNode;
};

export const EntityEditorShell = ({
    opened,
    zIndex,
    suspended = false,
    onClose,
    title,
    subtitle,
    status,
    dirty,
    loading,
    tabs,
    activeTab,
    onTabChange,
    footer,
    headerRight,
    size = 'xl',
    children,
}: Props) => {
    const { t } = useTranslation();
    const [confirmClose, setConfirmClose] = useState(false);

    const requestClose = () => {
        if (dirty && !confirmClose) {
            const ok = window.confirm(t('common.confirmCloseUnsaved'));

            if (!ok) return;
        }

        setConfirmClose(false);
        onClose();
    };

    return (
        <Drawer
            opened={opened}
            zIndex={zIndex}
            trapFocus={!suspended}
            closeOnEscape={!suspended}
            closeOnClickOutside={!suspended}
            onClose={requestClose}
            position='right'
            size={size}
            padding={0}
            title={
                <Group gap='sm' wrap='nowrap' justify='space-between' style={{ width: '100%' }}>
                    <Stack gap={2} style={{ minWidth: 0 }}>
                        <Group gap='sm' wrap='nowrap'>
                            <Text fw={650} truncate>
                                {title}
                            </Text>
                            {status && (
                                <Badge color={status.color || 'gray'} variant='light'>
                                    {status.label}
                                </Badge>
                            )}
                            {dirty && (
                                <Badge color='orange' variant='dot'>
                                    {t('components.entityEditorShell.unsaved')}
                                </Badge>
                            )}
                        </Group>
                        {subtitle && (
                            <Text size='xs' c='dimmed' truncate>
                                {subtitle}
                            </Text>
                        )}
                    </Stack>
                    {headerRight}
                </Group>
            }
            styles={{
                header: {
                    padding: '16px 20px',
                    borderBottom: '1px solid var(--mantine-color-gray-2)',
                },
                body: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'calc(100% - 70px)',
                    padding: 0,
                },
                title: { flex: 1, marginRight: 8 },
            }}
        >
            {loading ? (
                <Box p='xl'>
                    <Loader />
                </Box>
            ) : (
                <>
                    {tabs && tabs.length > 0 && (
                        <Box px='md' pt='md' pb='xs'>
                            <Tabs value={activeTab} onChange={(v) => v && onTabChange?.(v)}>
                                <Tabs.List>
                                    {tabs.map((tab) => (
                                        <Tabs.Tab key={tab.value} value={tab.value}>
                                            <Group gap={6} wrap='nowrap'>
                                                {tab.label}
                                                {tab.dirty && (
                                                    <Badge size='xs' color='orange' circle>
                                                        ·
                                                    </Badge>
                                                )}
                                            </Group>
                                        </Tabs.Tab>
                                    ))}
                                </Tabs.List>
                            </Tabs>
                        </Box>
                    )}

                    <Box style={{ flex: 1, overflow: 'auto' }} p='md'>
                        {children}
                    </Box>

                    {footer && (
                        <Box
                            px='md'
                            py='sm'
                            style={{
                                borderTop: '1px solid var(--mantine-color-gray-2)',
                                background: 'var(--mantine-color-body)',
                            }}
                        >
                            {footer}
                        </Box>
                    )}
                </>
            )}
        </Drawer>
    );
};

export const EditorFooter = ({
    onCancel,
    onSave,
    saving,
    saveLabel,
    left,
    disabled,
}: {
    onCancel: () => void;
    onSave?: () => void;
    saving?: boolean;
    saveLabel?: string;
    left?: React.ReactNode;
    disabled?: boolean;
}) => {
    const { t } = useTranslation();

    return (
        <Group justify='space-between'>
            <Group gap='sm'>{left}</Group>
            <Group gap='sm'>
                <Button variant='default' onClick={onCancel}>
                    {t('common.cancel')}
                </Button>
                {onSave && (
                    <Button loading={saving} disabled={disabled} onClick={onSave}>
                        {saveLabel ?? t('common.save')}
                    </Button>
                )}
            </Group>
        </Group>
    );
};
